"use client";

import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { RotateCcw, RefreshCw, Key, Hash } from "lucide-react";
import { UUIDGeneratorState, UUIDVersion } from "./types";

interface UUIDGeneratorFormProps {
  state: UUIDGeneratorState;
  updateState: (update: Partial<UUIDGeneratorState>) => void;
  onGenerate: () => void;
  onClear: () => void;
  onLoadSampleNamespace: () => void;
}

export default function UUIDGeneratorForm({
  state,
  updateState,
  onGenerate,
  onClear,
  onLoadSampleNamespace,
}: UUIDGeneratorFormProps) {
  const handleVersionChange = (value: UUIDVersion) => {
    updateState({ uuidVersion: value });
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(2000, Math.max(1, parseInt(e.target.value) || 1));
    updateState({ quantity: value });
  };

  const handleNamespaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateState({ namespace: e.target.value });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateState({ name: e.target.value });
  };

  const getVersionDescription = (version: UUIDVersion): string => {
    switch (version) {
      case "v1":
        return "Time-based UUIDs include timestamp and MAC address. Good for chronological ordering.";
      case "v3":
        return "MD5-based UUIDs are deterministic for the same namespace and name. Deprecated due to MD5 vulnerabilities.";
      case "v4":
        return "Random UUIDs are cryptographically secure and unpredictable. Most commonly used.";
      case "v5":
        return "SHA-1 based UUIDs are deterministic for the same namespace and name. Recommended over v3.";
      case "v6":
        return "Reordered timestamp UUIDs with better sorting than v1. Modern alternative to v1.";
      case "v7":
        return "Unix-time ordered UUIDs with timestamp and randomness. Recommended for new applications.";
      default:
        return "";
    }
  };

  return (
    <Card className="card-enhanced">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5" />
          <span>Generator Settings</span>
        </CardTitle>
        <CardDescription>
          Configure UUID generation parameters
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">UUID Version</label>
          <Select value={state.uuidVersion} onValueChange={handleVersionChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="v1">UUID v1 (Time-based)</SelectItem>
              <SelectItem value="v3">UUID v3 (MD5 based)</SelectItem>
              <SelectItem value="v4">UUID v4 (Random)</SelectItem>
              <SelectItem value="v5">UUID v5 (SHA-1 based)</SelectItem>
              <SelectItem value="v6">UUID v6 (Reordered timestamp)</SelectItem>
              <SelectItem value="v7">UUID v7 (Unix-time ordered)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Quantity</label>
          <Input
            type="number"
            min="1"
            max="2000"
            value={state.quantity}
            onChange={handleQuantityChange}
            className="w-full"
          />
          <p className="text-xs text-muted-foreground">Min: 1, Max: 2000</p>
        </div>

        {(state.uuidVersion === "v3" || state.uuidVersion === "v5") && (
          <>
            <div className="space-y-2">
              <label className="text-sm font-medium">Namespace UUID</label>
              <Input
                value={state.namespace}
                onChange={handleNamespaceChange}
                placeholder="Enter namespace UUID"
                className="w-full font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                For v3: Use DNS namespace (6ba7b810-9dad-11d1-80b4-00c04fd430c8)
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input
                value={state.name}
                onChange={handleNameChange}
                placeholder={`Enter name for UUID ${state.uuidVersion}`}
                className="w-full"
              />
            </div>

            <Button
              onClick={onLoadSampleNamespace}
              variant="outline"
              size="sm"
              className="hover-lift"
            >
              Load Sample Namespace
            </Button>
          </>
        )}

        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={onGenerate} 
            className="btn-enhanced hover-lift"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Generate UUIDs
          </Button>
          <Button 
            onClick={onClear} 
            variant="outline" 
            className="hover-lift"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>

        <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
          <Hash className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800 dark:text-blue-200">
            {getVersionDescription(state.uuidVersion)}
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}
